/**
 * Pure BNPL lifecycle decisions. No timers, fetch, window or DOM: time comes in as arguments.
 */

import type { PaymentKitErrors } from "../types";

export const SHORT_POPUP_CLOSED_GRACE_MS = 15_000;

/**
 * Long grace for providers whose popup handle can report `closed` while the customer still pays.
 * Afterpay's Stripe redirect sends COOP, so the handle is lost and the long wait stays.
 */
export const POPUP_CLOSED_GRACE_MS = 5 * 60 * 1000;

export const nextClosedSince = (
  closedSince: number | undefined,
  popupClosed: boolean,
  now: number,
): number | undefined => closedSince ?? (popupClosed ? now : undefined);

export const shouldGiveUp = (closedSince: number | undefined, now: number, graceMs: number): boolean =>
  closedSince !== undefined && now - closedSince >= graceMs;

/** Extra wait, past the grace, before unanswered status requests may end the submit. */
export const UNREAD_CAP_EXTRA_MS = 30_000;

export const shouldGiveUpUnread = (
  closedSince: number | undefined,
  lastReadAt: number,
  now: number,
  graceMs: number,
): boolean => shouldGiveUp(closedSince, now, graceMs) && now - lastReadAt >= graceMs + UNREAD_CAP_EXTRA_MS;

export type PollAction = "success" | "failed" | "giveUp" | "wait";

export type PollInput = {
  /** True when this tick got a status answer. False when the request aborted. */
  statusRead: boolean;
  lastReadAt: number;
  completed: boolean;
  status: "success" | "failed" | "cancelled";
  popupClosed: boolean;
  closedSince: number | undefined;
  now: number;
  graceMs: number;
};

export type PollDecision = { action: PollAction; closedSince: number | undefined; lastReadAt: number };

export const decidePollAction = (input: PollInput): PollDecision => {
  const closedSince = nextClosedSince(input.closedSince, input.popupClosed, input.now);
  const lastReadAt = input.statusRead ? input.now : input.lastReadAt;
  if (input.completed) {
    return { action: input.status === "success" ? "success" : "failed", closedSince, lastReadAt };
  }
  const giveUp = input.statusRead
    ? shouldGiveUp(closedSince, input.now, input.graceMs)
    : shouldGiveUpUnread(closedSince, lastReadAt, input.now, input.graceMs);
  return { action: giveUp ? "giveUp" : "wait", closedSince, lastReadAt };
};

export type SettleState = "open" | "settled";

export const settleOnce = (state: SettleState): { state: "settled"; accepted: boolean } => ({
  state: "settled",
  accepted: state === "open",
});

export type BnplSubmitResult =
  | { data: { [key: string]: unknown }; errors?: never; tornDown?: never; superseded?: never; joined?: true }
  | { data?: never; errors: PaymentKitErrors; tornDown?: true; superseded?: true; joined?: true };

export type SubmitSettler = {
  promise: Promise<BnplSubmitResult>;
  settle(result: BnplSubmitResult): boolean;
  isSettled(): boolean;
  /** The shopper moved to another payment method. A later success still reports; anything else is silent. */
  detach(): void;
  /** The shopper clicked the same BNPL again while its popup is open. Errors report again. */
  reattach(): void;
  isDetached(): boolean;
};

/** How a pending BNPL submit gives way when the shopper starts another payment. */
export type SupersedeMode = "abandon" | "detach";

/**
 * Another BNPL submit replaces this one outright: its own /start guard returns an earlier
 * success. A card or wallet submit only detaches it and leaves its popup open: the guard refuses
 * that charge when this payment already succeeded, so this submit must stay alive to report the
 * success.
 */
export const supersedeModeFor = (targetIsBnpl: boolean): SupersedeMode => (targetIsBnpl ? "abandon" : "detach");

/** A detached submit reports only a success. Its errors settle silently. */
export const resultAfterDetach = (result: BnplSubmitResult, detached: boolean): BnplSubmitResult =>
  detached && result.errors ? { ...result, superseded: true } : result;

const capitalize = (name: string): string => name.charAt(0).toUpperCase() + name.slice(1);

export const buildClosedResult = (name: string): BnplSubmitResult => ({
  errors: { [name]: `${capitalize(name)} popup closed by user` } as PaymentKitErrors,
});

export const buildSupersededResult = (name: string): BnplSubmitResult => ({
  errors: { [name]: `${capitalize(name)} checkout was replaced by another payment` } as PaymentKitErrors,
  superseded: true,
});

export const buildTeardownResult = (name: string): BnplSubmitResult => ({
  errors: { [name]: `${capitalize(name)} checkout was torn down` } as PaymentKitErrors,
  tornDown: true,
});

const BEFORE_HAND_OFF = "bnplBeforeHandOff";

/**
 * Marks errors a BNPL submit returns before it hands off to the other BNPLs (invalid options, a
 * blocked popup, a rejected beforeConfirm). The mark is non-enumerable, so merchants see only the
 * message.
 */
export const markBeforeHandOff = <T extends object>(errors: T): T =>
  Object.defineProperty(errors, BEFORE_HAND_OFF, { value: true });

/** True when a BNPL submit failed before its hand-off, so an earlier BNPL is still pending. */
export const isBnplFailureBeforeHandOff = (errors: object): boolean => BEFORE_HAND_OFF in errors;
